import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  assignClient,
  createClientFlag,
  getClient,
  listClientMicroLoans,
  getClientMicroLoanTier,
  listClientFlags,
  listClientMicroLoanTierHistory,
  resolveClientFlag,
  updateClientMicroLoanTier,
  updateClient,
} from "../api/endpoints/clients"
import { listLoanRepayments, recordLoanRepayment } from "../api/endpoints/loans"
import { createTicket } from "../api/endpoints/tickets"
import { listUsers } from "../api/endpoints/users"
import { useAuth } from "../stores/auth"
import { formatCurrency, formatDate, formatDateTime, formatDurationSeconds, formatEnumLabel } from "../utils/formatting"
import {
  Button,
  ContentCard,
  DataTable,
  Drawer,
  EmptyState,
  FieldList,
  FormField,
  Input,
  MetricGrid,
  Modal,
  SegmentedTabs,
  Select,
  StatCard,
  StatusPill,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Th,
  Textarea,
} from "./ui"

type ClientDetailTab =
  | "overview"
  | "micro_loan"
  | "repayments"
  | "interactions"
  | "next_of_kin"
  | "tickets_audit"

interface ClientEditFormState {
  fullName: string
  phoneNumber: string
  altPhone: string
  region: string
  physicalAddress: string
  portalStandardProductsEnabled: boolean
}

interface RepaymentFormState {
  amount: string
  paymentMethod: string
  transactionRef: string
  paymentDate: string
  notes: string
  reconciled: boolean
}

interface TicketFormState {
  ticketType: string
  priority: string
  subject: string
  description: string
}

interface ClientFlagFormState {
  reason: string
  note: string
}

const DEFAULT_CLIENT_EDIT_FORM: ClientEditFormState = {
  fullName: "",
  phoneNumber: "",
  altPhone: "",
  region: "",
  physicalAddress: "",
  portalStandardProductsEnabled: false,
}

const DEFAULT_TICKET_FORM: TicketFormState = {
  ticketType: "PAYMENT_ISSUE",
  priority: "MEDIUM",
  subject: "",
  description: "",
}

const DEFAULT_FLAG_FORM: ClientFlagFormState = {
  reason: "",
  note: "",
}

function toLocalDateTimeInputValue(date = new Date()) {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return localDate.toISOString().slice(0, 16)
}

function createDefaultRepaymentForm(): RepaymentFormState {
  return {
    amount: "",
    paymentMethod: "MOBILE_MONEY",
    transactionRef: "",
    paymentDate: toLocalDateTimeInputValue(),
    notes: "",
    reconciled: true,
  }
}

function parseMoneyInputToMinorUnits(value: string) {
  const normalized = Number.parseFloat(value)
  if (!Number.isFinite(normalized) || normalized <= 0) {
    return null
  }

  return Math.round(normalized * 100)
}

function overdueTone(category?: string | null): "neutral" | "info" | "warning" | "danger" {
  switch (category) {
    case "CATEGORY_C":
    case "CATEGORY_Z":
      return "danger"
    case "CATEGORY_B":
    case "CATEGORY_A":
      return "warning"
    case "S1":
      return "info"
    default:
      return "neutral"
  }
}

function pipelineTone(status?: string | null): "neutral" | "warning" | "info" | "success" | "danger" {
  switch (status) {
    case "PROMISE_TO_PAY":
      return "info"
    case "BROKEN_PROMISE":
      return "danger"
    case "DONE":
      return "success"
    case "NEW":
    case "OLD":
      return "warning"
    default:
      return "neutral"
  }
}

function repaymentStatusTone(status?: string): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "PAID":
      return "success"
    case "PARTIAL":
      return "warning"
    case "MISSED":
      return "danger"
    default:
      return "info"
  }
}

function ticketStatusTone(status?: string): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "RESOLVED":
      return "success"
    case "CLOSED":
      return "neutral"
    case "IN_PROGRESS":
      return "info"
    case "OPEN":
    case "AWAITING_INFO":
      return "warning"
    default:
      return "info"
  }
}

function flagStatusTone(status?: string): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "RESOLVED":
      return "success"
    case "OPEN":
      return "warning"
    default:
      return "neutral"
  }
}

function formatRate(value?: number | null) {
  return `${(((value ?? 0) as number) * 100).toFixed(2)}%`
}

function onTimeFlagTone(
  value?: boolean | number | null,
): "neutral" | "success" | "warning" | "danger" | "info" {
  if (value === true || value === 1) {
    return "success"
  }

  if (value === false || value === 0) {
    return "warning"
  }

  return "neutral"
}

function onTimeFlagLabel(value?: boolean | number | null) {
  if (value === true || value === 1) {
    return "On time"
  }

  if (value === false || value === 0) {
    return "Late"
  }

  return "Open"
}

function getPermissionValue(
  explicitValue: boolean | undefined,
  fallbackValue: boolean,
) {
  return explicitValue ?? fallbackValue
}

export function ClientDetailDrawer({
  clientId,
  open,
  onClose,
}: {
  clientId: string | null
  open: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<ClientDetailTab>("overview")
  const [editOpen, setEditOpen] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [ticketOpen, setTicketOpen] = useState(false)
  const [flagOpen, setFlagOpen] = useState(false)
  const [resolveFlagId, setResolveFlagId] = useState<string | null>(null)
  const [selectedAgentId, setSelectedAgentId] = useState("")
  const [clientEditForm, setClientEditForm] = useState<ClientEditFormState>(DEFAULT_CLIENT_EDIT_FORM)
  const [repaymentForm, setRepaymentForm] = useState<RepaymentFormState>(() => createDefaultRepaymentForm())
  const [repaymentFeedback, setRepaymentFeedback] = useState("")
  const [ticketForm, setTicketForm] = useState<TicketFormState>(DEFAULT_TICKET_FORM)
  const [flagForm, setFlagForm] = useState<ClientFlagFormState>(DEFAULT_FLAG_FORM)
  const [resolveNote, setResolveNote] = useState("")
  const [microTierAction, setMicroTierAction] = useState("OVERRIDE_TIER")
  const [microTierValue, setMicroTierValue] = useState("T1")
  const [microTierReason, setMicroTierReason] = useState("ADMIN_REMEDIATION")
  const [microTierFreezeReason, setMicroTierFreezeReason] = useState("MANUAL_FREEZE")
  const [microTierNote, setMicroTierNote] = useState("")
  const [microLoanHistoryPage, setMicroLoanHistoryPage] = useState(1)

  const role = user?.role
  const canEditClients = getPermissionValue(user?.permissions?.canEditClients, role === "SUPER_ADMIN")
  const canAssignClients = getPermissionValue(
    user?.permissions?.canAssignClients,
    role === "SUPER_ADMIN" || role === "ADMIN",
  )
  const canFlagClients = getPermissionValue(
    user?.permissions?.canFlagClients,
    role === "SUPER_ADMIN" || role === "ADMIN",
  )
  const canRaiseClientTickets = getPermissionValue(
    user?.permissions?.canRaiseClientTickets,
    role === "SUPER_ADMIN" || role === "ADMIN",
  )
  const canRecordRepayments = getPermissionValue(
    user?.permissions?.canRecordRepayments,
    role === "SUPER_ADMIN" || role === "ADMIN" || role === "OPS_MANAGER",
  )
  const canManageMicroTier = role === "SUPER_ADMIN" || role === "ADMIN"

  const detailQuery = useQuery({
    queryKey: ["admin-client-detail", clientId],
    queryFn: () => getClient(clientId!),
    enabled: open && Boolean(clientId),
  })

  const detail = detailQuery.data?.data
  const loanId = detail?.loanSummary?.loanId ?? null

  const repaymentsQuery = useQuery({
    queryKey: ["admin-client-repayments", loanId],
    queryFn: () => listLoanRepayments(loanId!),
    enabled: open && Boolean(loanId),
  })

  const flagsQuery = useQuery({
    queryKey: ["admin-client-flags", clientId],
    queryFn: () => listClientFlags(clientId!),
    enabled: open && Boolean(clientId),
  })
  const microTierQuery = useQuery({
    queryKey: ["admin-client-micro-tier", clientId],
    queryFn: () => getClientMicroLoanTier(clientId!),
    enabled: open && Boolean(clientId) && role !== "VERIFICATION_STAFF",
  })
  const microTierHistoryQuery = useQuery({
    queryKey: ["admin-client-micro-tier-history", clientId],
    queryFn: () => listClientMicroLoanTierHistory(clientId!),
    enabled: open && Boolean(clientId) && role !== "VERIFICATION_STAFF",
  })
  const microLoanHistoryQuery = useQuery({
    queryKey: ["admin-client-micro-loans", clientId, microLoanHistoryPage],
    queryFn: () => listClientMicroLoans(clientId!, { page: microLoanHistoryPage, perPage: 6 }),
    enabled: open && Boolean(clientId) && role !== "VERIFICATION_STAFF",
  })

  const agentsQuery = useQuery({
    queryKey: ["users", "agents", "client-drawer"],
    queryFn: () => listUsers({ role: "AGENT", per_page: "100" }),
    enabled: open && canAssignClients,
  })

  const repayments = repaymentsQuery.data?.data
  const reviewFlags = flagsQuery.data?.data ?? detail?.reviewFlags ?? []
  const microTier = microTierQuery.data?.data ?? null
  const microTierHistory = microTierHistoryQuery.data?.data ?? []
  const microLoanHistory = microLoanHistoryQuery.data?.data ?? []
  const microLoanHistoryMeta = microLoanHistoryQuery.data?.meta
  const openFlags = reviewFlags.filter((flag: any) => flag.status === "OPEN")
  const resolveTarget = useMemo(
    () => reviewFlags.find((flag: any) => flag.flagId === resolveFlagId) ?? null,
    [resolveFlagId, reviewFlags],
  )

  async function invalidateClientContext() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin-client-detail", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["admin-client-flags", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["admin-client-micro-tier", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["admin-client-micro-tier-history", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["admin-client-micro-loans", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["clients"] }),
      queryClient.invalidateQueries({ queryKey: ["client-detail", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["collections-client-detail", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["collections-clients"] }),
      queryClient.invalidateQueries({ queryKey: ["collections-agent-clients"] }),
      queryClient.invalidateQueries({ queryKey: ["assignment-queue"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-agent-clients"] }),
    ])

    if (loanId) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-client-repayments", loanId] }),
        queryClient.invalidateQueries({ queryKey: ["loan-repayments", loanId] }),
        queryClient.invalidateQueries({ queryKey: ["collections-repayments", loanId] }),
      ])
    }
  }

  const updateMutation = useMutation({
    mutationFn: (payload: ClientEditFormState) => updateClient(clientId!, payload),
    onSuccess: async () => {
      setEditOpen(false)
      await invalidateClientContext()
    },
  })

  const assignMutation = useMutation({
    mutationFn: (agentId: string) => assignClient(clientId!, agentId),
    onSuccess: async () => {
      setAssignOpen(false)
      setSelectedAgentId("")
      await invalidateClientContext()
    },
  })

  const recordRepaymentMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => recordLoanRepayment(loanId!, payload),
    onSuccess: async () => {
      setRepaymentForm(createDefaultRepaymentForm())
      setRepaymentFeedback("Repayment recorded successfully.")
      await invalidateClientContext()
    },
    onError: (error) => {
      setRepaymentFeedback((error as Error).message)
    },
  })

  const createTicketMutation = useMutation({
    mutationFn: (payload: TicketFormState) =>
      createTicket({
        ticketType: payload.ticketType,
        priority: payload.priority,
        subject: payload.subject,
        description: payload.description,
        clientId,
        loanId,
      }),
    onSuccess: async () => {
      setTicketOpen(false)
      setTicketForm(DEFAULT_TICKET_FORM)
      await invalidateClientContext()
    },
  })

  const createFlagMutation = useMutation({
    mutationFn: (payload: ClientFlagFormState) => createClientFlag(clientId!, payload),
    onSuccess: async () => {
      setFlagOpen(false)
      setFlagForm(DEFAULT_FLAG_FORM)
      await invalidateClientContext()
    },
  })

  const resolveFlagMutation = useMutation({
    mutationFn: (flagId: string) =>
      resolveClientFlag(clientId!, flagId, {
        status: "RESOLVED",
        resolutionNote: resolveNote || undefined,
      }),
    onSuccess: async () => {
      setResolveFlagId(null)
      setResolveNote("")
      await invalidateClientContext()
    },
  })
  const updateMicroTierMutation = useMutation({
    mutationFn: () =>
      updateClientMicroLoanTier(clientId!, {
        action: microTierAction,
        ...(microTierAction === "OVERRIDE_TIER"
          ? {
              newTier: microTierValue,
              reason: microTierReason,
            }
          : {}),
        ...(microTierAction === "FREEZE"
          ? {
              freezeReason: microTierFreezeReason,
            }
          : {}),
        note: microTierNote,
      }),
    onSuccess: async () => {
      setMicroTierNote("")
      await invalidateClientContext()
    },
  })

  useEffect(() => {
    if (!open) {
      setActiveTab("overview")
      setEditOpen(false)
      setAssignOpen(false)
      setTicketOpen(false)
      setFlagOpen(false)
      setResolveFlagId(null)
      setSelectedAgentId("")
      setRepaymentFeedback("")
      setTicketForm(DEFAULT_TICKET_FORM)
      setFlagForm(DEFAULT_FLAG_FORM)
      setResolveNote("")
      setMicroTierNote("")
      setMicroLoanHistoryPage(1)
    }
  }, [open])

  useEffect(() => {
    setRepaymentForm(createDefaultRepaymentForm())
    setRepaymentFeedback("")
    setActiveTab("overview")
    setMicroLoanHistoryPage(1)
  }, [clientId])

  function openEditModal() {
    if (!detail) {
      return
    }

    setClientEditForm({
      fullName: detail.fullName ?? "",
      phoneNumber: detail.phoneNumber ?? "",
      altPhone: detail.altPhone ?? "",
      region: detail.region ?? "",
      physicalAddress: detail.physicalAddress ?? "",
      portalStandardProductsEnabled: Boolean(detail.portalStandardProductsEnabled),
    })
    setEditOpen(true)
  }

  function openAssignModal() {
    setSelectedAgentId(detail?.loanSummary?.assignedAgentId ?? "")
    setAssignOpen(true)
  }

  function openRepaymentTab() {
    setActiveTab("repayments")
    setRepaymentFeedback("")
  }

  function handleRepaymentSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!loanId) {
      return
    }

    const amount = parseMoneyInputToMinorUnits(repaymentForm.amount)
    if (!amount) {
      setRepaymentFeedback("Enter a valid repayment amount greater than zero.")
      return
    }

    recordRepaymentMutation.mutate({
      amount,
      paymentMethod: repaymentForm.paymentMethod,
      transactionRef: repaymentForm.transactionRef || undefined,
      paymentDate: new Date(repaymentForm.paymentDate).toISOString(),
      notes: repaymentForm.notes || undefined,
      reconciled: repaymentForm.reconciled,
    })
  }

  const drawerFooter =
    detail && role !== "VERIFICATION_STAFF" && role !== "AGENT" ? (
      <div className="flex flex-wrap items-center gap-3">
        {canEditClients ? (
          <Button variant="secondary" onClick={openEditModal}>
            Edit Client
          </Button>
        ) : null}
        {canAssignClients ? (
          <Button variant="secondary" onClick={openAssignModal}>
            {detail.loanSummary?.assignedAgentId ? "Reassign Client" : "Assign Client"}
          </Button>
        ) : null}
        {canFlagClients ? (
          <Button variant="secondary" onClick={() => setFlagOpen(true)}>
            Flag For Review
          </Button>
        ) : null}
        {canRaiseClientTickets ? (
          <Button variant="secondary" onClick={() => setTicketOpen(true)}>
            Raise Linked Ticket
          </Button>
        ) : null}
        {canRecordRepayments && loanId ? (
          <Button onClick={openRepaymentTab}>Record Repayment</Button>
        ) : null}
      </div>
    ) : null

  return (
    <>
      <Drawer
        title={detail?.fullName ?? "Client Detail"}
        subtitle={detail?.clientId ?? "Role-aware management surface"}
        open={open}
        onClose={onClose}
        widthClassName="w-[min(1080px,100vw)]"
        footer={drawerFooter}
      >
        {detailQuery.isLoading ? (
          <div className="space-y-3">
            <div className="h-6 w-40 animate-pulse rounded bg-slate-100" />
            <div className="h-28 animate-pulse rounded-2xl bg-slate-100" />
            <div className="h-64 animate-pulse rounded-2xl bg-slate-100" />
          </div>
        ) : !detail ? (
          <EmptyState title="Client not found" description="No matching client record was returned." />
        ) : (
          <div className="space-y-5 pb-4">
            <ContentCard
              title="Operations Summary"
              subtitle="Use this drawer to review the portfolio position and perform the actions allowed by your role."
              actions={
                canRecordRepayments && loanId ? (
                  <Button size="sm" onClick={openRepaymentTab}>
                    Record Repayment
                  </Button>
                ) : null
              }
            >
              <MetricGrid columns="xl:grid-cols-5">
                <StatCard
                  label="Outstanding"
                  value={formatCurrency(detail.loanSummary?.outstandingBalance ?? 0, detail.loanSummary?.currency)}
                  tone="slate"
                />
                <StatCard
                  label="Days Overdue"
                  value={detail.loanSummary?.daysOverdue ?? 0}
                  tone="red"
                />
                <StatCard
                  label="Penalties"
                  value={formatCurrency(detail.loanSummary?.totalPenalties ?? 0, detail.loanSummary?.currency)}
                  tone="amber"
                />
                <StatCard
                  label="Open Review Flags"
                  value={openFlags.length}
                  tone={openFlags.length > 0 ? "amber" : "slate"}
                />
                <StatCard
                  label="Assigned Agent"
                  value={detail.loanSummary?.assignedAgentName ?? "Unassigned"}
                  tone="blue"
                />
              </MetricGrid>

              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                <StatusPill tone={detail.kycStatus === "APPROVED" ? "success" : "warning"}>
                  {formatEnumLabel(detail.kycStatus)}
                </StatusPill>
                <StatusPill tone={overdueTone(detail.loanSummary?.overdueCategory)}>
                  {formatEnumLabel(detail.loanSummary?.overdueCategory)}
                </StatusPill>
                <StatusPill tone={pipelineTone(detail.loanSummary?.pipelineStatus)}>
                  {formatEnumLabel(detail.loanSummary?.pipelineStatus)}
                </StatusPill>
                {role === "OPS_MANAGER" ? (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Repayment capture only
                  </span>
                ) : null}
              </div>

              {role === "OPS_MANAGER" ? (
                <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                  Ops Manager can review the full client profile here and record repayments, but assignment,
                  flagging, and ticket actions stay disabled.
                </div>
              ) : null}
            </ContentCard>

            <SegmentedTabs<ClientDetailTab>
              value={activeTab}
              onChange={setActiveTab}
              options={[
                { value: "overview", label: "Overview" },
                {
                  value: "micro_loan",
                  label: "Micro Loan",
                  count:
                    microLoanHistoryMeta?.totalItems ??
                    microTierHistory.length ??
                    null,
                  tone: microTier?.isFrozen ? "warning" : "info",
                },
                {
                  value: "repayments",
                  label: "Repayments",
                  count: repayments?.summary?.paymentCount ?? repayments?.repayments?.length ?? null,
                  tone: "info",
                },
                {
                  value: "interactions",
                  label: "Interactions",
                  count: detail.interactionHistory?.length ?? 0,
                  tone: "warning",
                },
                {
                  value: "next_of_kin",
                  label: "Next Of Kin",
                  count: detail.nextOfKin?.length ?? 0,
                  tone: "neutral",
                },
                {
                  value: "tickets_audit",
                  label: "Tickets & Audit",
                  count: (detail.ticketSummary?.length ?? 0) + openFlags.length,
                  tone: openFlags.length > 0 ? "warning" : "neutral",
                },
              ]}
            />

            {activeTab === "overview" ? (
              <div className="space-y-5">
                <ContentCard
                  title="Client Profile"
                  subtitle="Identity and contact information for servicing, support, and collections."
                >
                  <FieldList
                    columns={3}
                    items={[
                      { label: "Client ID", value: detail.clientId, accent: true },
                      { label: "Phone", value: detail.phoneNumber },
                      { label: "Alternate phone", value: detail.altPhone ?? "-" },
                      { label: "Region", value: detail.region ?? "-" },
                      { label: "Physical address", value: detail.physicalAddress ?? "-" },
                      {
                        label: "Portal standard products",
                        value: (
                          <StatusPill
                            tone={detail.portalStandardProductsEnabled ? "info" : "neutral"}
                          >
                            {detail.portalStandardProductsEnabled ? "Enabled" : "Hidden"}
                          </StatusPill>
                        ),
                      },
                      { label: "Date of birth", value: formatDate(detail.dateOfBirth) },
                      { label: "KYC status", value: formatEnumLabel(detail.kycStatus) },
                      { label: "Created", value: formatDateTime(detail.createdAt) },
                      { label: "Updated", value: formatDateTime(detail.updatedAt) },
                    ]}
                  />
                </ContentCard>

                <ContentCard
                  title="Loan Summary"
                  subtitle="Latest loan position, assignment, and collections posture."
                >
                  {detail.loanSummary ? (
                    <FieldList
                      columns={3}
                      items={[
                        { label: "Loan ID", value: detail.loanSummary.loanId, accent: true },
                        { label: "Product", value: detail.loanSummary.productName },
                        {
                          label: "Principal",
                          value: formatCurrency(
                            detail.loanSummary.principalAmount ?? 0,
                            detail.loanSummary.currency,
                          ),
                        },
                        {
                          label: "Outstanding",
                          value: formatCurrency(
                            detail.loanSummary.outstandingBalance ?? 0,
                            detail.loanSummary.currency,
                          ),
                        },
                        {
                          label: "Penalties",
                          value: formatCurrency(
                            detail.loanSummary.totalPenalties ?? 0,
                            detail.loanSummary.currency,
                          ),
                        },
                        { label: "Lifecycle", value: formatEnumLabel(detail.loanSummary.lifecycleStage) },
                        {
                          label: "Overdue bucket",
                          value: (
                            <StatusPill tone={overdueTone(detail.loanSummary.overdueCategory)}>
                              {formatEnumLabel(detail.loanSummary.overdueCategory)}
                            </StatusPill>
                          ),
                        },
                        {
                          label: "Pipeline",
                          value: (
                            <StatusPill tone={pipelineTone(detail.loanSummary.pipelineStatus)}>
                              {formatEnumLabel(detail.loanSummary.pipelineStatus)}
                            </StatusPill>
                          ),
                        },
                        {
                          label: "Assigned agent",
                          value: detail.loanSummary.assignedAgentName ?? "Unassigned",
                        },
                        { label: "Due date", value: formatDate(detail.loanSummary.dueDate) },
                        { label: "Disbursed", value: formatDate(detail.loanSummary.disbursementDate) },
                        { label: "PTP date", value: formatDateTime(detail.loanSummary.ptpDate) },
                      ]}
                    />
                  ) : (
                    <EmptyState title="No active loan" description="This client has no active loan summary." />
                  )}
                </ContentCard>

                <ContentCard
                  title="Operational Snapshot"
                  subtitle="Recent review, ticket, and assignment signals for faster triage."
                >
                  <div className="grid gap-4 xl:grid-cols-3">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Review Flags
                      </p>
                      {reviewFlags.length ? (
                        <div className="mt-3 space-y-2">
                          {reviewFlags.slice(0, 3).map((flag: any) => (
                            <div key={flag.flagId} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-sm font-semibold text-slate-900">{flag.reason}</p>
                                <StatusPill tone={flagStatusTone(flag.status)}>{flag.status}</StatusPill>
                              </div>
                              <p className="mt-1 text-xs text-slate-500">{formatDateTime(flag.createdAt)}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-slate-500">No review flags recorded.</p>
                      )}
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Linked Tickets
                      </p>
                      {detail.ticketSummary?.length ? (
                        <div className="mt-3 space-y-2">
                          {detail.ticketSummary.slice(0, 3).map((ticket: any) => (
                            <div key={ticket.ticketId} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-sm font-semibold text-slate-900">{ticket.subject}</p>
                                <StatusPill tone={ticketStatusTone(ticket.status)}>{ticket.status}</StatusPill>
                              </div>
                              <p className="mt-1 text-xs text-slate-500">{ticket.ticketId}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-slate-500">No linked tickets recorded.</p>
                      )}
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Last Assignment
                      </p>
                      {detail.assignmentHistory?.[0] ? (
                        <div className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <p className="text-sm font-semibold text-slate-900">
                            {detail.assignmentHistory[0].agentName}
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            Assigned {formatDateTime(detail.assignmentHistory[0].assignedAt)}
                          </p>
                          <p className="mt-2 text-sm text-slate-600">
                            {detail.assignmentHistory[0].reason ?? "No assignment note provided."}
                          </p>
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-slate-500">No assignment history recorded.</p>
                      )}
                    </div>
                  </div>
                </ContentCard>
              </div>
            ) : null}

            {activeTab === "micro_loan" ? (
              <div className="space-y-5">
                <ContentCard
                  title="Micro Loan Tier"
                  subtitle="Quick-loan pricing, progression, and freeze state for this client."
                >
                  {microTierQuery.isLoading ? (
                    <p className="text-sm text-slate-500">Loading micro-loan tier...</p>
                  ) : microTier ? (
                    <div className="space-y-4">
                      <MetricGrid columns="xl:grid-cols-5">
                        <StatCard label="Current Tier" value={microTier.currentTier} tone="blue" />
                        <StatCard label="Tier Name" value={microTier.tierName} tone="slate" />
                        <StatCard
                          label="Cycles"
                          value={`${microTier.tierScore} / ${microTier.terms.requiredCycles}`}
                          tone="amber"
                        />
                        <StatCard label="Ceiling" value={microTier.terms.principalCeilingDisplay} tone="slate" />
                        <StatCard
                          label="Status"
                          value={microTier.isFrozen ? "Frozen" : "Active"}
                          tone={microTier.isFrozen ? "red" : "emerald"}
                        />
                      </MetricGrid>

                      <div
                        className={`rounded-xl border px-4 py-3 ${
                          microTier.isFrozen
                            ? "border-red-200 bg-red-50 text-red-900"
                            : "border-emerald-200 bg-emerald-50 text-emerald-900"
                        }`}
                      >
                        <p className="text-sm font-semibold">
                          {microTier.isFrozen
                            ? "This client's quick-loan tier is frozen."
                            : "This client can continue progressing through quick-loan tiers."}
                        </p>
                        <p className="mt-1 text-sm">
                          {microTier.isFrozen
                            ? `Freeze reason: ${formatEnumLabel(microTier.freezeReason)}`
                            : microTier.nextTier
                              ? `${microTier.nextTier.remainingCycles} more on-time closures unlock ${microTier.nextTier.tier}.`
                              : "This client is already at the highest quick-loan tier."}
                        </p>
                      </div>

                      <FieldList
                        columns={3}
                        items={[
                          {
                            label: "Tenor range",
                            value: `${microTier.terms.minTenorDays}-${microTier.terms.maxTenorDays} days`,
                          },
                          {
                            label: "Default tenor",
                            value: `${microTier.terms.defaultTenorDays} days`,
                          },
                          {
                            label: "Application fee",
                            value: formatRate(microTier.terms.applicationFeeRate),
                          },
                          { label: "Interest rate", value: formatRate(microTier.terms.interestRate) },
                          {
                            label: "Cycle progress",
                            value: `${microTier.tierScore} / ${microTier.terms.requiredCycles}`,
                          },
                          {
                            label: "Freeze reason",
                            value: microTier.freezeReason ? formatEnumLabel(microTier.freezeReason) : "-",
                          },
                          {
                            label: "Next tier",
                            value: microTier.nextTier
                              ? `${microTier.nextTier.tier} in ${microTier.nextTier.remainingCycles} cycles`
                              : "Max tier",
                          },
                          {
                            label: "Latest change",
                            value: microTier.latestChange
                              ? `${formatEnumLabel(microTier.latestChange.direction)} · ${formatDateTime(
                                  microTier.latestChange.createdAt,
                                )}`
                              : "-",
                          },
                        ]}
                      />

                      {microTier.activeMicroLoan ? (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <p className="text-sm font-semibold text-slate-900">
                            Active quick loan: {microTier.activeMicroLoan.loanId}
                          </p>
                          <p className="mt-1 text-sm text-slate-600">
                            {formatCurrency(microTier.activeMicroLoan.principalAmount ?? 0, "USD")} principal ·{" "}
                            {formatEnumLabel(microTier.activeMicroLoan.lifecycleStage)}
                          </p>
                          <p className="mt-2 text-sm text-slate-500">
                            Due {formatDate(microTier.activeMicroLoan.dueDate)} · Net disbursed{" "}
                            {formatCurrency(microTier.activeMicroLoan.netDisbursedAmount ?? 0, "USD")}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <EmptyState
                      title="No micro-loan tier data"
                      description="This client does not currently expose a micro-loan tier profile."
                    />
                  )}
                </ContentCard>

                <ContentCard
                  title="Tier History"
                  subtitle="Promotions, demotions, freezes, and manual overrides."
                >
                  {microTierHistoryQuery.isLoading ? (
                    <p className="text-sm text-slate-500">Loading tier history...</p>
                  ) : microTierHistory.length ? (
                    <div className="overflow-x-auto">
                      <DataTable>
                        <TableHead>
                          <Th>When</Th>
                          <Th>Direction</Th>
                          <Th>Tier Change</Th>
                          <Th>Trigger Loan</Th>
                          <Th>Actor</Th>
                          <Th>Reason</Th>
                          <Th>Note</Th>
                        </TableHead>
                        <TableBody>
                          {microTierHistory.map((entry: any) => (
                            <TableRow key={entry.id}>
                              <Td>{formatDateTime(entry.createdAt)}</Td>
                              <Td>
                                <StatusPill
                                  tone={
                                    entry.direction === "PROMOTION"
                                      ? "success"
                                      : entry.direction === "DEMOTION" || entry.direction === "FREEZE"
                                        ? "warning"
                                        : "info"
                                  }
                                >
                                  {formatEnumLabel(entry.direction)}
                                </StatusPill>
                              </Td>
                              <Td>
                                <div className="font-medium text-slate-800">
                                  {entry.tierFrom ?? "-"} to {entry.tierTo}
                                </div>
                              </Td>
                              <Td>{entry.triggerLoanPublicId ?? "-"}</Td>
                              <Td>{entry.actorName ?? entry.actorPublicId ?? "System"}</Td>
                              <Td>{formatEnumLabel(entry.reason)}</Td>
                              <Td className="max-w-[260px] text-slate-600">{entry.note ?? "-"}</Td>
                            </TableRow>
                          ))}
                        </TableBody>
                      </DataTable>
                    </div>
                  ) : (
                    <EmptyState
                      title="No tier history"
                      description="No micro-loan tier changes have been recorded for this client yet."
                    />
                  )}
                </ContentCard>

                <ContentCard
                  title="Micro Loan History"
                  subtitle="Previous quick loans, disbursed net amounts, and on-time closure performance."
                  actions={
                    microLoanHistoryMeta ? (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={microLoanHistoryPage <= 1}
                          onClick={() =>
                            setMicroLoanHistoryPage((current) => Math.max(1, current - 1))
                          }
                        >
                          Previous
                        </Button>
                        <span className="text-xs text-slate-500">
                          Page {microLoanHistoryMeta.page} of {microLoanHistoryMeta.totalPages}
                        </span>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={
                            microLoanHistoryPage >= (microLoanHistoryMeta.totalPages ?? 1)
                          }
                          onClick={() => setMicroLoanHistoryPage((current) => current + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    ) : null
                  }
                >
                  {microLoanHistoryQuery.isLoading ? (
                    <p className="text-sm text-slate-500">Loading micro-loan history...</p>
                  ) : microLoanHistory.length ? (
                    <div className="overflow-x-auto">
                      <DataTable>
                        <TableHead>
                          <Th>Loan</Th>
                          <Th>Status</Th>
                          <Th>Tier</Th>
                          <Th>Principal</Th>
                          <Th>Net Disbursed</Th>
                          <Th>Fee</Th>
                          <Th>Tenor</Th>
                          <Th>Due</Th>
                          <Th>Closed</Th>
                          <Th>On-Time</Th>
                        </TableHead>
                        <TableBody>
                          {microLoanHistory.map((entry: any) => (
                            <TableRow key={entry.loanId}>
                              <Td>
                                {role === "SUPER_ADMIN" ? (
                                  <button
                                    type="button"
                                    className="font-medium text-blue-700 hover:text-blue-800"
                                    onClick={() =>
                                      navigate(`/disbursements?search=${encodeURIComponent(entry.loanId)}`)
                                    }
                                  >
                                    {entry.loanId}
                                  </button>
                                ) : (
                                  <span className="font-medium text-slate-800">{entry.loanId}</span>
                                )}
                              </Td>
                              <Td>
                                <StatusPill tone={repaymentStatusTone(entry.lifecycleStage)}>
                                  {formatEnumLabel(entry.lifecycleStage)}
                                </StatusPill>
                              </Td>
                              <Td>{entry.microLoanTier ?? "-"}</Td>
                              <Td>{formatCurrency(entry.principalAmount ?? 0, "USD")}</Td>
                              <Td>{formatCurrency(entry.netDisbursedAmount ?? 0, "USD")}</Td>
                              <Td>{formatCurrency(entry.applicationFeeAmount ?? 0, "USD")}</Td>
                              <Td>{entry.termDays} days</Td>
                              <Td>{formatDate(entry.dueDate)}</Td>
                              <Td>{formatDate(entry.closedAt)}</Td>
                              <Td>
                                <StatusPill tone={onTimeFlagTone(entry.onTimeFlag)}>
                                  {onTimeFlagLabel(entry.onTimeFlag)}
                                </StatusPill>
                              </Td>
                            </TableRow>
                          ))}
                        </TableBody>
                      </DataTable>
                    </div>
                  ) : (
                    <EmptyState
                      title="No micro-loan history"
                      description="This client has not originated any quick loans yet."
                    />
                  )}
                </ContentCard>

                {canManageMicroTier ? (
                  <ContentCard
                    title="Admin Controls"
                    subtitle="Override the micro-loan tier or change freeze state. Admin changes require a detailed note."
                  >
                    <div className="grid gap-4 md:grid-cols-2">
                      <FormField label="Action">
                        <Select
                          onChange={(event) => setMicroTierAction(event.target.value)}
                          value={microTierAction}
                        >
                          <option value="OVERRIDE_TIER">Override tier</option>
                          <option value="FREEZE">Freeze</option>
                          <option value="UNFREEZE">Unfreeze</option>
                        </Select>
                      </FormField>

                      {microTierAction === "OVERRIDE_TIER" ? (
                        <FormField label="New tier">
                          <Select
                            onChange={(event) => setMicroTierValue(event.target.value)}
                            value={microTierValue}
                          >
                            <option value="T1">T1</option>
                            <option value="T2">T2</option>
                            <option value="T3">T3</option>
                            <option value="T4">T4</option>
                            <option value="T5">T5</option>
                          </Select>
                        </FormField>
                      ) : microTierAction === "FREEZE" ? (
                        <FormField label="Freeze reason">
                          <Select
                            onChange={(event) => setMicroTierFreezeReason(event.target.value)}
                            value={microTierFreezeReason}
                          >
                            <option value="MANUAL_FREEZE">Manual freeze</option>
                            <option value="WRITE_OFF">Write-off</option>
                          </Select>
                        </FormField>
                      ) : (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                          This action removes the freeze and keeps the current tier.
                        </div>
                      )}

                      {microTierAction === "OVERRIDE_TIER" ? (
                        <FormField label="Override reason" className="md:col-span-2">
                          <Select
                            onChange={(event) => setMicroTierReason(event.target.value)}
                            value={microTierReason}
                          >
                            <option value="ADMIN_REMEDIATION">Admin remediation</option>
                            <option value="PROMOTIONAL">Promotional</option>
                            <option value="CORRECTING_SYSTEM_ERROR">Correcting system error</option>
                            <option value="OTHER">Other</option>
                          </Select>
                        </FormField>
                      ) : null}

                      <FormField label="Audit note" className="md:col-span-2">
                        <Textarea
                          onChange={(event) => setMicroTierNote(event.target.value)}
                          rows={4}
                          value={microTierNote}
                        />
                      </FormField>

                      <div className="md:col-span-2 flex flex-wrap items-center gap-3">
                        <Button
                          disabled={updateMicroTierMutation.isPending || microTierNote.trim().length < 20}
                          onClick={() => updateMicroTierMutation.mutate()}
                        >
                          {updateMicroTierMutation.isPending ? "Saving..." : "Apply Micro Loan Update"}
                        </Button>
                        {updateMicroTierMutation.isError ? (
                          <p className="text-sm text-red-700">
                            {(updateMicroTierMutation.error as Error)?.message}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </ContentCard>
                ) : null}
              </div>
            ) : null}

            {activeTab === "repayments" ? (
              !detail.loanSummary ? (
                <EmptyState title="No active loan" description="Repayment capture is only available when a client has an active loan." />
              ) : repaymentsQuery.isLoading ? (
                <div className="space-y-3">
                  <div className="h-24 animate-pulse rounded-2xl bg-slate-100" />
                  <div className="h-48 animate-pulse rounded-2xl bg-slate-100" />
                </div>
              ) : !repayments ? (
                <EmptyState title="Repayment data unavailable" description="The repayment schedule could not be loaded for this loan." />
              ) : (
                <div className="space-y-5">
                  <MetricGrid columns="xl:grid-cols-4">
                    <StatCard
                      label="Total Paid"
                      value={formatCurrency(repayments.summary.totalPaidToDate, repayments.summary.currency)}
                      tone="blue"
                    />
                    <StatCard
                      label="Remaining"
                      value={formatCurrency(repayments.summary.totalRemaining, repayments.summary.currency)}
                      tone="red"
                    />
                    <StatCard label="Payments" value={repayments.summary.paymentCount} tone="slate" />
                    <StatCard
                      label="Last Payment"
                      value={formatDate(repayments.summary.lastPaymentAt)}
                      tone="amber"
                    />
                  </MetricGrid>

                  {canRecordRepayments ? (
                    <ContentCard
                      title={repayments.summary.totalRemaining > 0 ? "Record Repayment" : "Loan Settled"}
                      subtitle="Capture a repayment against the active loan. This is the direct collect-from-client action for management users."
                    >
                      {repayments.summary.totalRemaining > 0 ? (
                        <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={handleRepaymentSubmit}>
                          <FormField label="Amount">
                            <Input
                              min="0"
                              onChange={(event) =>
                                setRepaymentForm((current) => ({ ...current, amount: event.target.value }))
                              }
                              placeholder="0.00"
                              step="0.01"
                              type="number"
                              value={repaymentForm.amount}
                            />
                          </FormField>
                          <FormField label="Payment method">
                            <Select
                              onChange={(event) =>
                                setRepaymentForm((current) => ({
                                  ...current,
                                  paymentMethod: event.target.value,
                                }))
                              }
                              value={repaymentForm.paymentMethod}
                            >
                              <option value="MOBILE_MONEY">Mobile money</option>
                              <option value="BANK_TRANSFER">Bank transfer</option>
                              <option value="CASH">Cash</option>
                              <option value="CARD">Card</option>
                              <option value="OTHER">Other</option>
                            </Select>
                          </FormField>
                          <FormField label="Payment date">
                            <Input
                              onChange={(event) =>
                                setRepaymentForm((current) => ({
                                  ...current,
                                  paymentDate: event.target.value,
                                }))
                              }
                              type="datetime-local"
                              value={repaymentForm.paymentDate}
                            />
                          </FormField>
                          <FormField label="Transaction reference">
                            <Input
                              onChange={(event) =>
                                setRepaymentForm((current) => ({
                                  ...current,
                                  transactionRef: event.target.value,
                                }))
                              }
                              value={repaymentForm.transactionRef}
                            />
                          </FormField>
                          <FormField label="Notes" className="md:col-span-2">
                            <Textarea
                              onChange={(event) =>
                                setRepaymentForm((current) => ({ ...current, notes: event.target.value }))
                              }
                              rows={3}
                              value={repaymentForm.notes}
                            />
                          </FormField>
                          <label className="inline-flex items-center gap-2 text-sm text-slate-700 md:col-span-2">
                            <input
                              checked={repaymentForm.reconciled}
                              onChange={(event) =>
                                setRepaymentForm((current) => ({
                                  ...current,
                                  reconciled: event.target.checked,
                                }))
                              }
                              type="checkbox"
                            />
                            Mark as reconciled immediately
                          </label>
                          <div className="md:col-span-2 flex flex-wrap items-center gap-3">
                            <Button disabled={recordRepaymentMutation.isPending} type="submit">
                              {recordRepaymentMutation.isPending ? "Recording..." : "Record Repayment"}
                            </Button>
                            {repaymentFeedback ? (
                              <p
                                className={`text-sm ${
                                  recordRepaymentMutation.isError ? "text-red-700" : "text-emerald-700"
                                }`}
                              >
                                {repaymentFeedback}
                              </p>
                            ) : null}
                          </div>
                        </form>
                      ) : (
                        <p className="text-sm text-emerald-700">
                          This loan is fully settled. No additional repayments are required.
                        </p>
                      )}
                    </ContentCard>
                  ) : null}

                  <ContentCard
                    title="Repayment Schedule"
                    subtitle="Expected and actual repayment performance for the active loan."
                  >
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs">
                        <thead>
                          <tr className="border-b border-slate-100 bg-slate-50 font-bold uppercase text-slate-500">
                            <th className="px-4 py-3">Payment #</th>
                            <th className="px-4 py-3">Expected date</th>
                            <th className="px-4 py-3">Expected amount</th>
                            <th className="px-4 py-3">Actual date</th>
                            <th className="px-4 py-3">Actual amount</th>
                            <th className="px-4 py-3">Variance</th>
                            <th className="px-4 py-3">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {repayments.schedule.map((entry: any) => (
                            <tr key={entry.paymentNumber}>
                              <td className="px-4 py-3">{entry.paymentNumber}</td>
                              <td className="px-4 py-3">{formatDate(entry.expectedDate)}</td>
                              <td className="px-4 py-3">
                                {formatCurrency(entry.expectedAmount, repayments.summary.currency)}
                              </td>
                              <td className="px-4 py-3">{formatDate(entry.actualDate)}</td>
                              <td className="px-4 py-3">
                                {formatCurrency(entry.actualAmount, repayments.summary.currency)}
                              </td>
                              <td className="px-4 py-3">
                                {formatCurrency(entry.variance, repayments.summary.currency)}
                              </td>
                              <td className="px-4 py-3">
                                <StatusPill tone={repaymentStatusTone(entry.status)}>
                                  {entry.status}
                                </StatusPill>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </ContentCard>

                  <ContentCard
                    title="Repayment History"
                    subtitle="Recorded repayment events and reconciliation status."
                  >
                    {repayments.repayments.length ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="border-b border-slate-100 bg-slate-50 font-bold uppercase text-slate-500">
                              <th className="px-4 py-3">Repayment</th>
                              <th className="px-4 py-3">Date</th>
                              <th className="px-4 py-3">Amount</th>
                              <th className="px-4 py-3">Method</th>
                              <th className="px-4 py-3">Recorded by</th>
                              <th className="px-4 py-3">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {repayments.repayments.map((entry: any) => (
                              <tr key={entry.repaymentId}>
                                <td className="px-4 py-3">
                                  <strong>{entry.repaymentId}</strong>
                                  <div className="text-xs text-slate-500">
                                    {entry.transactionRef ?? "No reference"}
                                  </div>
                                </td>
                                <td className="px-4 py-3">{formatDateTime(entry.paymentDate)}</td>
                                <td className="px-4 py-3">
                                  {formatCurrency(entry.amount, entry.currency)}
                                </td>
                                <td className="px-4 py-3">{formatEnumLabel(entry.paymentMethod)}</td>
                                <td className="px-4 py-3">{entry.recordedBy?.fullName ?? "System"}</td>
                                <td className="px-4 py-3">
                                  <StatusPill tone={entry.reconciled ? "success" : "warning"}>
                                    {entry.reconciled ? "RECONCILED" : "PENDING"}
                                  </StatusPill>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <EmptyState title="No repayment activity" description="No repayments have been recorded for this loan yet." />
                    )}
                  </ContentCard>
                </div>
              )
            ) : null}

            {activeTab === "interactions" ? (
              <ContentCard
                title="Interaction History"
                subtitle="Recent communication and pipeline events associated with this client."
              >
                {detail.interactionHistory?.length ? (
                  <div className="space-y-3">
                    {detail.interactionHistory.map((entry: any) => (
                      <div key={entry.interactionId} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">
                              {formatEnumLabel(entry.interactionType)} · {formatEnumLabel(entry.outcome)}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {formatDateTime(entry.createdAt)} · {entry.agentName ?? "Unknown agent"}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span>Duration {formatDurationSeconds(entry.durationSeconds)}</span>
                            <span>Follow up {formatDate(entry.followUpDate)}</span>
                            <span>PTP {formatDate(entry.ptpDate)}</span>
                          </div>
                        </div>
                        <p className="mt-3 text-sm text-slate-600">{entry.notes ?? "No notes captured."}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState title="No interaction history" description="No interaction events have been recorded for this client yet." />
                )}
              </ContentCard>
            ) : null}

            {activeTab === "next_of_kin" ? (
              <ContentCard
                title="Next Of Kin"
                subtitle="Reference contacts available during support and recovery work."
              >
                {detail.nextOfKin?.length ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    {detail.nextOfKin.map((entry: any) => (
                      <div key={`${entry.fullName}-${entry.phoneNumber}`} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                        <p className="font-semibold text-slate-900">{entry.fullName}</p>
                        <p className="mt-1 text-sm text-slate-600">{entry.relationship}</p>
                        <p className="mt-1 text-sm text-slate-600">{entry.phoneNumber}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState title="No next-of-kin contacts" description="This client does not have next-of-kin records on file." />
                )}
              </ContentCard>
            ) : null}

            {activeTab === "tickets_audit" ? (
              <div className="space-y-5">
                <ContentCard
                  title="Review Flags"
                  subtitle="Super Admin and Admin can flag a client for review and resolve the flag after follow-up."
                  actions={
                    canFlagClients ? (
                      <Button size="sm" variant="secondary" onClick={() => setFlagOpen(true)}>
                        Flag For Review
                      </Button>
                    ) : null
                  }
                >
                  {flagsQuery.isLoading ? (
                    <p className="text-sm text-slate-500">Loading review flags...</p>
                  ) : reviewFlags.length ? (
                    <div className="space-y-3">
                      {reviewFlags.map((flag: any) => (
                        <div key={flag.flagId} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-sm font-semibold text-slate-900">{flag.reason}</p>
                                <StatusPill tone={flagStatusTone(flag.status)}>{flag.status}</StatusPill>
                              </div>
                              <p className="mt-1 text-xs text-slate-500">
                                {flag.flagId} · Created {formatDateTime(flag.createdAt)} by {flag.createdByName ?? "Unknown"}
                              </p>
                            </div>
                            {canFlagClients && flag.status === "OPEN" ? (
                              <Button size="sm" variant="outline" onClick={() => setResolveFlagId(flag.flagId)}>
                                Resolve Flag
                              </Button>
                            ) : null}
                          </div>
                          <p className="mt-3 text-sm text-slate-600">{flag.note ?? "No review note provided."}</p>
                          {flag.status === "RESOLVED" ? (
                            <p className="mt-3 text-xs text-slate-500">
                              Resolved {formatDateTime(flag.resolvedAt)} by {flag.resolvedByName ?? "Unknown"}.
                              {flag.resolutionNote ? ` ${flag.resolutionNote}` : ""}
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState title="No review flags" description="This client has not been flagged for review." />
                  )}
                </ContentCard>

                <ContentCard
                  title="Linked Tickets"
                  subtitle="Operational tickets raised against this client and their current handling state."
                  actions={
                    canRaiseClientTickets ? (
                      <Button size="sm" variant="secondary" onClick={() => setTicketOpen(true)}>
                        Raise Linked Ticket
                      </Button>
                    ) : null
                  }
                >
                  {detail.ticketSummary?.length ? (
                    <div className="space-y-3">
                      {detail.ticketSummary.map((ticket: any) => (
                        <div key={ticket.ticketId} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{ticket.subject}</p>
                              <p className="mt-1 text-xs text-slate-500">
                                {ticket.ticketId} · {formatEnumLabel(ticket.ticketType)} · Assigned to{" "}
                                {ticket.assignedToName ?? "Unassigned"}
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <StatusPill tone={ticketStatusTone(ticket.status)}>{ticket.status}</StatusPill>
                              <StatusPill tone={ticket.priority === "URGENT" || ticket.priority === "HIGH" ? "danger" : "info"}>
                                {ticket.priority}
                              </StatusPill>
                            </div>
                          </div>
                          <p className="mt-3 text-xs text-slate-500">Updated {formatDateTime(ticket.updatedAt)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState title="No linked tickets" description="No ticket has been raised from this client context yet." />
                  )}
                </ContentCard>

                <ContentCard
                  title="Assignment History"
                  subtitle="Client ownership changes captured over time."
                >
                  {detail.assignmentHistory?.length ? (
                    <div className="space-y-3">
                      {detail.assignmentHistory.map((row: any) => (
                        <div key={row.assignmentId} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold text-slate-900">{row.agentName}</p>
                              <p className="text-xs text-slate-500">
                                Assigned {formatDateTime(row.assignedAt)}
                                {row.unassignedAt ? ` · Unassigned ${formatDateTime(row.unassignedAt)}` : ""}
                              </p>
                            </div>
                            <p className="text-xs text-slate-500">{row.assignedByName ?? "System"}</p>
                          </div>
                          <p className="mt-2 text-sm text-slate-600">{row.reason ?? "No note provided."}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState title="No assignment history" description="No assignment events have been recorded for this client." />
                  )}
                </ContentCard>
              </div>
            ) : null}
          </div>
        )}
      </Drawer>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Client"
        description="Update the core client profile fields available to Super Admin."
      >
        <form
          className="grid grid-cols-1 gap-4 md:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault()
            updateMutation.mutate(clientEditForm)
          }}
        >
          <FormField label="Full name">
            <Input
              required
              value={clientEditForm.fullName}
              onChange={(event) =>
                setClientEditForm((current) => ({ ...current, fullName: event.target.value }))
              }
            />
          </FormField>
          <FormField label="Phone number">
            <Input
              required
              value={clientEditForm.phoneNumber}
              onChange={(event) =>
                setClientEditForm((current) => ({ ...current, phoneNumber: event.target.value }))
              }
            />
          </FormField>
          <FormField label="Alternate phone">
            <Input
              value={clientEditForm.altPhone}
              onChange={(event) =>
                setClientEditForm((current) => ({ ...current, altPhone: event.target.value }))
              }
            />
          </FormField>
          <FormField label="Region">
            <Input
              value={clientEditForm.region}
              onChange={(event) =>
                setClientEditForm((current) => ({ ...current, region: event.target.value }))
              }
            />
          </FormField>
          <FormField label="Physical address" className="md:col-span-2">
            <Textarea
              rows={3}
              value={clientEditForm.physicalAddress}
              onChange={(event) =>
                setClientEditForm((current) => ({
                  ...current,
                  physicalAddress: event.target.value,
                }))
              }
            />
          </FormField>
          <label className="md:col-span-2 flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <input
              checked={clientEditForm.portalStandardProductsEnabled}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              onChange={(event) =>
                setClientEditForm((current) => ({
                  ...current,
                  portalStandardProductsEnabled: event.target.checked,
                }))
              }
              type="checkbox"
            />
            <span>
              Enable standard loan products in the borrower portal for this client. Quick loans remain the default
              borrower experience.
            </span>
          </label>
          <div className="md:col-span-2 flex flex-wrap items-center gap-3">
            <Button disabled={updateMutation.isPending} type="submit">
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
            {updateMutation.isError ? (
              <p className="text-sm text-red-700">{(updateMutation.error as Error).message}</p>
            ) : null}
          </div>
        </form>
      </Modal>

      <Modal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        title={detail?.loanSummary?.assignedAgentId ? "Reassign Client" : "Assign Client"}
        description="Choose the destination agent for this client’s active loan."
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (!selectedAgentId) {
              return
            }
            assignMutation.mutate(selectedAgentId)
          }}
        >
          <FormField label="Agent">
            <Select value={selectedAgentId} onChange={(event) => setSelectedAgentId(event.target.value)}>
              <option value="">Select agent</option>
              {(agentsQuery.data?.data ?? []).map((agent: any) => (
                <option key={agent.userId} value={agent.userId}>
                  {agent.fullName}
                </option>
              ))}
            </Select>
          </FormField>
          <div className="flex flex-wrap items-center gap-3">
            <Button disabled={assignMutation.isPending || selectedAgentId.length === 0} type="submit">
              {assignMutation.isPending ? "Saving..." : "Confirm Assignment"}
            </Button>
            {assignMutation.isError ? (
              <p className="text-sm text-red-700">{(assignMutation.error as Error).message}</p>
            ) : null}
          </div>
        </form>
      </Modal>

      <Modal
        open={ticketOpen}
        onClose={() => setTicketOpen(false)}
        title="Raise Linked Ticket"
        description="This ticket will be linked to the client and the active loan when one exists."
      >
        <form
          className="grid grid-cols-1 gap-4 md:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault()
            createTicketMutation.mutate(ticketForm)
          }}
        >
          <FormField label="Ticket type">
            <Select
              value={ticketForm.ticketType}
              onChange={(event) =>
                setTicketForm((current) => ({ ...current, ticketType: event.target.value }))
              }
            >
              <option value="CLIENT_DISPUTE">Client dispute</option>
              <option value="PAYMENT_ISSUE">Payment issue</option>
              <option value="ACCOUNT_ISSUE">Account issue</option>
              <option value="TECHNICAL_ISSUE">Technical issue</option>
              <option value="ESCALATION">Escalation</option>
              <option value="OTHER">Other</option>
            </Select>
          </FormField>
          <FormField label="Priority">
            <Select
              value={ticketForm.priority}
              onChange={(event) =>
                setTicketForm((current) => ({ ...current, priority: event.target.value }))
              }
            >
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="URGENT">Urgent</option>
            </Select>
          </FormField>
          <FormField label="Subject" className="md:col-span-2">
            <Input
              required
              value={ticketForm.subject}
              onChange={(event) =>
                setTicketForm((current) => ({ ...current, subject: event.target.value }))
              }
            />
          </FormField>
          <FormField label="Description" className="md:col-span-2">
            <Textarea
              required
              rows={4}
              value={ticketForm.description}
              onChange={(event) =>
                setTicketForm((current) => ({ ...current, description: event.target.value }))
              }
            />
          </FormField>
          <div className="md:col-span-2 flex flex-wrap items-center gap-3">
            <Button disabled={createTicketMutation.isPending} type="submit">
              {createTicketMutation.isPending ? "Creating..." : "Create Ticket"}
            </Button>
            {createTicketMutation.isError ? (
              <p className="text-sm text-red-700">{(createTicketMutation.error as Error).message}</p>
            ) : null}
          </div>
        </form>
      </Modal>

      <Modal
        open={flagOpen}
        onClose={() => setFlagOpen(false)}
        title="Flag Client For Review"
        description="Create a lightweight review flag for follow-up and management oversight."
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            createFlagMutation.mutate(flagForm)
          }}
        >
          <FormField label="Reason">
            <Input
              required
              value={flagForm.reason}
              onChange={(event) =>
                setFlagForm((current) => ({ ...current, reason: event.target.value }))
              }
            />
          </FormField>
          <FormField label="Note">
            <Textarea
              rows={4}
              value={flagForm.note}
              onChange={(event) =>
                setFlagForm((current) => ({ ...current, note: event.target.value }))
              }
            />
          </FormField>
          <div className="flex flex-wrap items-center gap-3">
            <Button disabled={createFlagMutation.isPending} type="submit">
              {createFlagMutation.isPending ? "Saving..." : "Create Flag"}
            </Button>
            {createFlagMutation.isError ? (
              <p className="text-sm text-red-700">{(createFlagMutation.error as Error).message}</p>
            ) : null}
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(resolveTarget)}
        onClose={() => {
          setResolveFlagId(null)
          setResolveNote("")
        }}
        title="Resolve Review Flag"
        description="Close the active review flag after the issue has been addressed."
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (!resolveTarget) {
              return
            }
            resolveFlagMutation.mutate(resolveTarget.flagId)
          }}
        >
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-sm font-semibold text-slate-900">{resolveTarget?.reason}</p>
            <p className="mt-2 text-sm text-slate-600">{resolveTarget?.note ?? "No review note provided."}</p>
          </div>
          <FormField label="Resolution note">
            <Textarea
              rows={4}
              value={resolveNote}
              onChange={(event) => setResolveNote(event.target.value)}
            />
          </FormField>
          <div className="flex flex-wrap items-center gap-3">
            <Button disabled={resolveFlagMutation.isPending} type="submit">
              {resolveFlagMutation.isPending ? "Resolving..." : "Resolve Flag"}
            </Button>
            {resolveFlagMutation.isError ? (
              <p className="text-sm text-red-700">{(resolveFlagMutation.error as Error).message}</p>
            ) : null}
          </div>
        </form>
      </Modal>
    </>
  )
}
